import {
  App,
  Checkbox,
  Divider,
  Form,
  InputNumber,
  Radio,
  Select,
  Space,
  Tabs,
} from 'antd';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import UserSelect from '@/components/UserSelect';
import {
  DEFAULT_TOTAL_AMOUNT_PRECISION,
  TotalAmountPrecisionNames,
} from '@/constants/totalAmountPrecision';
import { batchUpdateCustomerConfig } from '@/services/ant-design-pro/api';
import { AUDIT_NODES, uniformAuditFields } from './auditFields';
import ConfigExcelImportPanel from './ConfigExcelImportPanel';
import { CUSTOMER_CONFIG_EXCEL } from './excelCodes';
import {
  type BatchMode,
  type BatchScenarioPanelHandle,
  type BatchScenarioPanelProps,
  filterValidCustomers,
  SWITCH_OPTIONS,
} from './types';

type ConfigMode = 'set' | 'clear';

const AutoAuditBatchPanel = forwardRef<
  BatchScenarioPanelHandle,
  BatchScenarioPanelProps
>(({ customers, active, onDirty, onFooterChange }, ref) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [batchMode, setBatchMode] = useState<BatchMode>('uniform');
  const [submitting, setSubmitting] = useState(false);
  const [configMode, setConfigMode] = useState<ConfigMode>('set');
  const [updatePrecision, setUpdatePrecision] = useState(false);

  const precisionOptions = useMemo(
    () =>
      Object.entries(TotalAmountPrecisionNames).map(([value, label]) => ({
        value: Number(value),
        label,
      })),
    [],
  );

  useEffect(() => {
    if (!active) return;
    form.setFieldsValue({
      configMode: 'set',
      totalAmountPrecision: DEFAULT_TOTAL_AMOUNT_PRECISION,
    });
    setBatchMode('uniform');
    setConfigMode('set');
    setUpdatePrecision(false);
    setSubmitting(false);
  }, [active, form]);

  useEffect(() => {
    if (!active) return;
    onFooterChange({
      showOk: batchMode === 'uniform',
      submitting,
    });
  }, [active, batchMode, submitting, onFooterChange]);

  useImperativeHandle(ref, () => ({
    submit: async () => {
      try {
        if (batchMode !== 'uniform') {
          throw new Error('请在「统一修改」中确认，或使用 Excel / 场景规则页内的导入');
        }
        const values = await form.validateFields();
        const auditFields = uniformAuditFields(values);
        const hasSwitchChange =
          values.secondReviewEnabled !== undefined ||
          values.thirdReviewEnabled !== undefined ||
          values.sealEnabled !== undefined;
        if (
          !updatePrecision &&
          !hasSwitchChange &&
          Object.keys(auditFields).length === 0
        ) {
          throw new Error('请至少选择一项要修改的参数');
        }
        const items = filterValidCustomers(customers).map((customer) => ({
          ...auditFields,
          customerId: customer.id as string | number,
          totalAmountPrecisionSpecified: updatePrecision,
          totalAmountPrecision: updatePrecision
            ? values.configMode === 'clear'
              ? null
              : values.totalAmountPrecision
            : undefined,
          secondReviewEnabled: values.secondReviewEnabled,
          thirdReviewEnabled: values.thirdReviewEnabled,
          sealEnabled: values.sealEnabled,
        }));
        if (items.length === 0) throw new Error('请选择客户');

        setSubmitting(true);
        const response = await batchUpdateCustomerConfig({ items });
        if (!response.success) {
          throw new Error(response.message || '批量编辑客户参数失败');
        }
        const result = response.result;
        if ((result?.successCount ?? 0) > 0) onDirty();
        if ((result?.failedCount ?? 0) > 0) {
          message.warning(
            `批量编辑完成：成功 ${result?.successCount ?? 0} 条，失败 ${result?.failedCount ?? 0} 条`,
          );
          return false;
        }
        message.success(`已成功修改 ${result?.successCount ?? 0} 个客户`);
        return true;
      } catch (error) {
        if (error && typeof error === 'object' && 'errorFields' in error) {
          return false;
        }
        message.error(
          error instanceof Error ? error.message : '批量编辑客户参数失败',
        );
        return false;
      } finally {
        setSubmitting(false);
      }
    },
  }));

  return (
    <Tabs
      activeKey={batchMode}
      onChange={(key) => setBatchMode(key as BatchMode)}
      items={[
        {
          key: 'uniform',
          label: '统一修改',
          children: (
            <Form form={form} layout="vertical">
              <Form.Item>
                <Checkbox
                  checked={updatePrecision}
                  onChange={(event) => setUpdatePrecision(event.target.checked)}
                >
                  修改报告总价展示精度
                </Checkbox>
              </Form.Item>
              {updatePrecision && (
                <>
                  <Form.Item
                    name="configMode"
                    label="精度配置方式"
                    rules={[{ required: true }]}
                  >
                    <Radio.Group
                      onChange={(event) => setConfigMode(event.target.value)}
                    >
                      <Radio value="set">设置精度</Radio>
                      <Radio value="clear">清除客户配置</Radio>
                    </Radio.Group>
                  </Form.Item>
                  {configMode === 'set' && (
                    <Form.Item
                      name="totalAmountPrecision"
                      label="报告总价展示精度"
                      rules={[{ required: true }]}
                    >
                      <Select options={precisionOptions} />
                    </Form.Item>
                  )}
                </>
              )}
              <div>
                仅修改客户默认配置；未选择的项目保持原值，清空后继承系统配置。
              </div>
              {AUDIT_NODES.map((n) => (
                <React.Fragment key={n.key}>
                  <Divider orientation="left">{n.label}</Divider>
                  <Form.Item name={n.key + 'Enabled'} label="自动审核">
                    <Select
                      allowClear
                      placeholder="不修改"
                      options={SWITCH_OPTIONS}
                    />
                  </Form.Item>
                  <Form.Item name={n.key + 'TimeMode'} label="等待时间">
                    <Select
                      allowClear
                      placeholder="不修改"
                      options={[
                        { value: 'set', label: '设置时间' },
                        { value: 'clear', label: '清空并继承系统配置' },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item noStyle shouldUpdate>
                    {() =>
                      form.getFieldValue(n.key + 'TimeMode') === 'set' && (
                        <Space align="start" wrap>
                          <Form.Item
                            name={n.key + 'MinMinutes'}
                            label="最小等待分钟数"
                            rules={[
                              {
                                required: true,
                                type: 'integer',
                                min: 0,
                                max: 2147483647,
                              },
                            ]}
                          >
                            <InputNumber
                              min={0}
                              max={2147483647}
                              precision={0}
                            />
                          </Form.Item>
                          <Form.Item
                            name={n.key + 'MaxRandomMinutes'}
                            label="最大随机分钟数"
                            rules={[
                              {
                                required: true,
                                type: 'integer',
                                min: 1,
                                max: 2147483647,
                              },
                            ]}
                          >
                            <InputNumber
                              min={1}
                              max={2147483647}
                              precision={0}
                            />
                          </Form.Item>
                        </Space>
                      )
                    }
                  </Form.Item>
                  <Form.Item name={n.auditor + 'Mode'} label="默认审批人">
                    <Select
                      allowClear
                      placeholder="不修改"
                      options={[
                        { value: 'set', label: '指定审批人' },
                        { value: 'clear', label: '清空并继承系统配置' },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item noStyle shouldUpdate>
                    {() =>
                      form.getFieldValue(n.auditor + 'Mode') === 'set' && (
                        <Form.Item
                          name={n.auditor}
                          label="选择审批人"
                          rules={[{ required: true }]}
                        >
                          <UserSelect />
                        </Form.Item>
                      )
                    }
                  </Form.Item>
                </React.Fragment>
              ))}
            </Form>
          ),
        },
        {
          key: 'excel',
          label: 'Excel批量修改',
          children: (
            <ConfigExcelImportPanel
              {...CUSTOMER_CONFIG_EXCEL.autoAuditConfig}
              description="下载模版填写后上传，空白表示不修改；时间两列须成对填写或同时填写“清空”；审批人填写用户ID或“清空”。"
              onSuccess={onDirty}
            />
          ),
        },
        {
          key: 'rules',
          label: '场景规则导入',
          children: (
            <ConfigExcelImportPanel
              {...CUSTOMER_CONFIG_EXCEL.autoAuditRule}
              description="下载场景规则模版填写后上传；仅新增规则，已有相同条件时报冲突。"
              onSuccess={onDirty}
            />
          ),
        },
      ]}
    />
  );
});

AutoAuditBatchPanel.displayName = 'AutoAuditBatchPanel';

export default AutoAuditBatchPanel;
